<template>
  <div class="actions-collapsed-list">
    <a
      class="btn btn-function exit-collapsed-list"
      @click="collapseActions"
    >
      <i
        class="glyphicon glyphicon-chevron-left"
        aria-hidden="true"
        tooltip-placement="right"
        uib-tooltip="Cancel"
      />
    </a>

    <div class="action-icon">
      <a
        class="btn btn-list-function"
        @click="showForm"
      >
        <i
          v-tooltip.bottom.hover="$t('EDIT')"
          class="fa fa-edit"
          aria-hidden="true"
        />
        <span
          v-t="'EDIT'"
          class="hint-text-mobile"
        />
      </a>
    </div>
    <div class="action-icon">
      <a
        :disabled="disabled"
        class="btn btn-list-function"
        @click="displayDeleteModal"
      >
        <i
          v-tooltip.bottom.hover="$t('DELETE')"
          class="fa fa-trash"
          aria-hidden="true"
        />
        <span
          v-t="'DELETE'"
          class="hint-text-mobile"
        />
      </a>
    </div>
    <div class="action-icon">
      <a
        :disabled="disabled"
        class="btn btn-list-function"
        @click="validate"
      >
        <i
          v-tooltip.bottom.hover="$t('VALIDATE')"
          class="fa fa-refresh"
          aria-hidden="true"
        />
        <span
          v-t="'VALIDATE'"
          class="hint-text-mobile"
        />
      </a>
    </div>

    <!-- Delete Confirmation Modal Start -->
    <ConfirmationModal 
      v-if="showDeleteModal"
      :title="$t('REFERENCES_MODAL_TITLE')"
      :cancel-button-text="$t('CANCEL')"
      :confirm-action-text="$t('DELETE')"
      :confirm-button-disabled="pending || error"
      :confirm-button-loading="deleting"
      @confirm="confirmDeleteModal"
      @close="hideDeleteModal"
    >
      <div v-if="pending || error">
        <div>
          <Loader v-if="pending" />
          <span v-t="checkReferencesText" />
        </div>
      </div>

      <p
        v-if="!pending && !error && references && !references.hasReferences"
        v-t="'LABEL_REFERENCES_EMPTY'"
      />

      <div v-if="!pending && references && references.hasReferences">        
        <span v-html="$t('LABEL_DELETE_CONFIRMATION_MESSAGE', { name: currentAuthConnection.name })">
          <br>
        </span>

        <div class="well well-sm m-t-10 m-b-0">
          <div class="form-horizontal">
            <!-- List Of Linked Campaigns -->
            <div
              v-if="references.dynamicinboundcampaignsettings && references.dynamicinboundcampaignsettings.length"
              class="row"
            >
              <label class="col-md-3 control-label">
                <strong v-t="'CAMPAIGNS'" />
              </label>
              <div class="col-md-8">
                <ul class="list-unstyled form-control-static list-text-overflow">
                  <li
                    v-for="campaign in references.dynamicinboundcampaignsettings"
                    :key="campaign.dynamicinboundcampaignsettingId"
                  >
                    <a
                      v-tooltip.right="$t('TT_VIEW_CAMPAIGN')"
                      target="_blank"
                      href="javascript:;"
                      @click.prevent="goToCampaign(campaign.dynamicinboundcampaignsettingId)"
                    >
                      <span
                        class="display-text"
                        :title="campaign.dynamicinboundcampaignsettingName"
                      >
                        {{ campaign.dynamicinboundcampaignsettingName }}
                      </span>
                      <i class="fa fa-external-link m-l-5" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- List Of Linked Campaigns End -->

            <!-- List Of Linked Pixels -->
            <div
              v-if="references.eventcallbacks"
              class="row"
            >
              <label class="col-md-3 control-label">
                <strong v-t="'PIXELS'" />
              </label>
              <div class="col-md-8">
                <ul class="list-unstyled form-control-static list-text-overflow">
                  <li
                    v-for="eventCallback in references.eventcallbacks"
                    :key="eventCallback.eventcallbackId"
                  >
                    <a
                      v-tooltip.right="$t('TT_VIEW_PIXEL')"
                      target="_blank"
                      href="javascript:;"
                      @click.prevent="goToPixel(eventCallback.eventcallbackId)"
                    >
                      <span
                        class="display-text"
                        :title="eventCallback.eventcallbackName"
                      >
                        {{ eventCallback.eventcallbackName }}
                      </span>
                      <i class="fa fa-external-link m-l-5" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- List Of Linked Pixels  End -->
            <!-- List Of Linked RTTs -->
            <div
              v-if="references.pingtreetargets"
              class="row"
            >
              <label class="col-md-3 control-label">
                <strong v-t="'RING_TREE_TARGETS'" />
              </label>
              <div class="col-md-8">
                <ul class="list-unstyled form-control-static list-text-overflow">
                  <li
                    v-for="pingtreetarget in references.pingtreetargets"
                    :key="pingtreetarget.pingtreetargetId"
                  >
                    <a
                      v-tooltip.right="$t('TT_VIEW_RTT')"
                      target="_blank"
                      href="javascript:;"
                      @click.prevent="goToRtt(pingtreetarget.pingtreetargetId)"
                    >
                      <span
                        class="display-text"
                        :title="pingtreetarget.pingtreetargetName"
                      >
                        {{ pingtreetarget.pingtreetargetName }}
                      </span>
                      <i class="fa fa-external-link m-l-5" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- List Of Linked RTTs End -->
          </div>
        </div>
      </div>
    </ConfirmationModal>
    <!-- Delete Confirmation Modal End -->
  </div>
</template>

<script>
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';
import Loader from '@/core/components/Loader';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { $ngStateService, accountsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';

export default {
  name: 'AuthConnectionsActions',
  components: {
    ConfirmationModal,
    Loader
  },
  props:{
    currentAuthConnection: {
      type: Object,
      default() {
        return {};
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    getReferencesStatus: {
      type: Object,
      default: function() {
        return {};
      }
    },
    deleteAuthConnectionStatus: {
      type: Object,
      default: function() {
        return {};
      }
    },
    references: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  data() {
    return {
      showDeleteModal: false,
      showPopOver: false,
      placement: 'bottomleft'
    };
  },
  computed: {
    currentUserName() { return accountsService.currentUserName; },
    checkReferencesText() {
      if(this.pending) {
        return 'LABEL_DELETE_MODAL_MESSAGE_ASSOCIATION_LOADING';
      }
      if(this.error) {
        return 'LABEL_DELETE_MODAL_MESSAGE_ASSOCIATION_ERROR';
      }

      return '';
    },
    pending() { return this.getReferencesStatus.status == STATUS_PENDING; },
    error() { return this.getReferencesStatus.status == STATUS_ERROR; },
    deleting() { return this.deleteAuthConnectionStatus.status == STATUS_PENDING; }
  },
  methods:{
    togglePopover() {
      this.showPopOver = !this.showPopOver;
    },
    displayDeleteModal() {
      if (!this.disabled) {
        this.$emit('open-delete', this.currentAuthConnection.id);
        this.showDeleteModal = true;
      }
    },
    hideDeleteModal() {
      this.showDeleteModal = false;
      this.references = null;
    },
    confirmDeleteModal() {
      this.$emit('confirm-delete', this.currentAuthConnection.id);
    },    
    showForm() {
      this.$emit('edit', this.currentAuthConnection.id);
    },
    collapseActions() {
      this.$emit('collapse-actions', this.currentAuthConnection.id);
    },    
    goToCampaign(id) {
      var url = $ngStateService.href('dashboard.campaign', { campaignId: id});
      window.open(url,'_blank'); // Open new tab
    },
    goToPixel(id) {
      var url = $ngStateService.href('dashboard.callbacks', { callBackId: id});
      window.open(url,'_blank'); // Open new tab
    },
    goToRtt(id) {
      var url = $ngStateService.href('dashboard.ringtreetarget.edit', { pingTreeTargetId: id});
      window.open(url,'_blank'); // Open new tab
    },
    validate() {
      this.$emit('validate', this.currentAuthConnection.id);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>

<style lang="scss">
form.user {
  .multi-select-lg {
    width: 100%;
  }

  .v-select .vs__selected {
    height: 32px;
    padding: 8px 8px 7px 10px;
    border-bottom: 1px solid transparent;
  }
}

.popover.left > .arrow:after {
  border-left-color: var(--component-in-app-notifications__popover)
}
</style>